import { NavLink } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';

import { Icon } from '@/lib/Icon';
import { cn } from '@/lib/cn';
import { useClinicNavUnread } from '@/features/messaging/useMessagingUnread';

/**
 * MobileBottomNav — fixed-bottom mono tab bar shown only below md.
 * Left slot: System glyph (currently inert; could open the overflow
 * drawer or route to /app later). Three primary tabs (STACK, SHOP,
 * LEARN) + a `⋯` button that opens the overflow drawer with the rest
 * of the routes (Home, Plan, Clinic, Orders, Account).
 */

const PRIMARY_TABS = [
  { to: '/app/protocols', label: 'STACK' },
  { to: '/app/shop', label: 'SHOP' },
  { to: '/app/learn', label: 'LEARN' },
] as const;

// Guided-tour spotlight anchors (features/tour/GuidedTour) — mobile steps.
const TOUR_TARGETS: Partial<Record<string, string>> = {
  '/app/protocols': 'stack-tab-m',
  '/app/shop': 'shop-tab-m',
};

export function MobileBottomNav({
  onOpenOverflow,
}: {
  onOpenOverflow: () => void;
}) {
  // Clinic lives in the overflow drawer, not the primary mobile nav.
  // Show a small dot on the `⋯` button so unread messages aren't
  // hidden behind a tap. Sidebar still shows the precise count on desktop.
  // Same count as the Clinic badge in the overflow drawer so the two never
  // disagree (care-team messaging counts both teams).
  const unreadCount = useClinicNavUnread();
  const hasUnread = unreadCount > 0;

  return (
    <nav
      aria-label="Primary"
      className="md:hidden fixed bottom-0 inset-x-0 z-sidebar h-50 border-t border-line-subtle bg-[#ECECEC] flex items-stretch"
    >
      <NavLink
        to="/app"
        end
        aria-label="Home"
        className="w-50 shrink-0 flex items-center justify-center text-ink hover:bg-slate"
      >
        <div className="w-25">
          <Icon name="Mark Line" viewBox="0 0 32 30" />
        </div>
      </NavLink>
      {PRIMARY_TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          data-tour={TOUR_TARGETS[t.to]}
          className={({ isActive }) =>
            cn(
              'flex-1 flex items-center justify-center type-label-sm tracking-wide',
              isActive ? 'text-ink' : 'text-ink-muted hover:text-ink',
            )
          }
        >
          {t.label}
        </NavLink>
      ))}
      <button
        type="button"
        onClick={onOpenOverflow}
        data-tour="more-button"
        aria-label={hasUnread ? 'More (unread messages)' : 'More'}
        className="relative w-50 shrink-0 flex items-center justify-center text-[#5E5E5E] hover:text-ink border-l border-line-subtle"
      >
        <div className="relative px-5 bg-[#D9D9D9] h-15 flex items-center justify-center">
          <MoreHorizontal size={20} strokeWidth={1.5} />
          {hasUnread && (
            <span
              aria-hidden
              className="absolute -top-8 -right-6 min-w-16 h-16 px-4 rounded-pill bg-accent flex items-center justify-center font-mono text-10 leading-none text-white tabular-nums"
            >
              {unreadCount}
            </span>
          )}
        </div>
      </button>
    </nav>
  );
}
